import { isEqual, capitalize } from 'element-ui/src/utils/util'
import { isDef } from 'element-ui/src/utils/shared'

let uid = 0

export default class Node {
	constructor (data, config, parentNode) {
		this.data = data
		this.config = config
		this.parent = parentNode || null
		this.level = !this.parent ? 1 : this.parent.level + 1
		this.uid = uid++

		this.initState()
		this.initChildren()
	}

	initState () {
		const { value: valueKey, label: labelKey } = this.config

		this.value = this.data[valueKey]
		this.label = this.data[labelKey]
		this.pathNodes = this.calculatePathNodes()
		this.path = this.pathNodes.map(node => node.value)
		this.pathLabels = this.pathNodes.map(node => node.label)

		// lazy load
		this.loading = false
		this.loaded = false
	}

	initChildren () {
		const { config } = this
		const childrenKey = config.children
		const childrenData = this.data[childrenKey]
		this.hasChildren = Array.isArray(childrenData)
		this.children = (childrenData || []).map(
			child => new Node(child, config, this)
		)
	}

	get isDisabled () {
		const { data, parent, config } = this
		const { checkStrictly } = config
		if (typeof config.disabled === 'function') {
			return (
				config.disabled(data) || (!checkStrictly && parent && parent.isDisabled)
			)
		} else {
			return (
				data[config.disabled] || (!checkStrictly && parent && parent.isDisabled)
			)
		}
	}

	get isLeaf () {
		const { data, loaded, hasChildren, children } = this
		const { lazy, leaf: leafKey } = this.config
		if (lazy) {
			const isLeaf = isDef(data[leafKey])
				? data[leafKey]
				: loaded
					? !children.length
					: false
			this.hasChildren = !isLeaf
			return isLeaf
		}
		return !hasChildren
	}

	calculatePathNodes () {
		const nodes = [this]
		let parent = this.parent

		while (parent) {
			nodes.unshift(parent)
			parent = parent.parent
		}

		return nodes
	}

	getPath () {
		return this.path
	}

	getValue () {
		return this.value
	}

	getValueByOption () {
		return this.config.emitPath ? this.getPath() : this.getValue()
	}

	getText (allLevels, separator) {
		return allLevels ? this.pathLabels.join(separator) : this.label
	}

	isSameNode (checkedValue) {
		const value = this.getValueByOption()
		if (this.config.multiple) {
			if (Array.isArray(checkedValue)) {
				return checkedValue.some(val => isEqual(val, value))
			} else if (checkedValue instanceof Set) {
				return checkedValue.has(value)
			}
		}
		return isEqual(checkedValue, value)
	}

	broadcast (event, ...args) {
		const handlerName = `onParent${capitalize(event)}`

		this.children.forEach(child => {
			if (child) {
				// bottom up
				child.broadcast(event, ...args)
				child[handlerName] && child[handlerName](...args)
			}
		})
	}

	emit (event, ...args) {
		const { parent } = this
		const handlerName = `onChild${capitalize(event)}`
		if (parent) {
			parent[handlerName] && parent[handlerName](event, ...args)
			parent.emit(event, ...args)
		}
	}

	onParentCheck (checked) {
		if (!this.isDisabled) {
			this.setCheckState(checked)
		}
	}

	onChildCheck (checked) {
		const { children } = this
		/**
		 * TODO 修改源代码
		 * 旧代码：
			const validChildren = children.filter(child => !child.isDisabled)

			this.setCheckState(
				this.config.checkStrictly
					? checked
					: validChildren.length
						? validChildren.every(child => child.checked)
						: false
			)
		* 1.旧代码使用filter，在数据量大且多次循环的情况下，生成一个array对性能开销过大
		* 	1.1改造直接使用chilren来判断，并在判断中直接使用isDisabled来进行判定，减少过滤这一步的操作
		* 2.旧代码使用every，实际测试中every比some性能开销会大一点
		* 	2.2使用some替换every，使性能上升
		 */
		this.setCheckState(
			this.config.checkStrictly
				? checked
				: children.length
					? !children.some(child => !child.checked && !child.isDisabled)
					: false
		)
	}

	setCheckState (checked) {
		this.checked = checked
		if (!this.config.checkStrictly) {
			const totalNum = this.children.length
			const checkedNum = this.children.reduce((c, p) => {
				const num = p.checked ? 1 : p.indeterminate ? 0.5 : 0
				return c + num
			}, 0)

			this.indeterminate = checkedNum !== totalNum && checkedNum > 0
		}
	}

	syncCheckState (checkedValue) {
		const value = this.getValueByOption()
		const checked = this.isSameNode(checkedValue, value)

		this.doCheck(checked)
	}

	doCheck (checked) {
		if (this.checked !== checked) {
			if (this.config.checkStrictly) {
				this.checked = checked
				/**
				 * TODO 组件改造标识
				 *  1.新增checkChildren字段
				 *  	1.1如果为true，则选择的时候自动勾选子级.否则单独勾选
				 *  2.并且进一步判断:如果isUseCtrl为true并且按住ctrl键，则反向操作
				 */
				if (this.config.checkChildren) {
					if (this.config.isUseCtrl) {
						if (!this.ctrlCheck) {
							this.broadcast('check', checked)
						}
					} else {
						this.broadcast('check', checked)
					}
				} else {
					if (this.config.isUseCtrl) {
						if (this.ctrlCheck) {
							this.broadcast('check', checked)
						}
					}
				}
			} else {
				// bottom up to unify the calculation of the indeterminate state
				this.broadcast('check', checked) // 选择子级
				this.setCheckState(checked) // 设置半选等状态
				this.emit('check') // 向上选择父级
			}
		}
	}
}
